// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import { useEffect, useMemo } from "react";

type ReviewCommentsPlace = {
  scrollTop: number;
  commentId: string | null;
};

// why: the review header Back leaves the review route, which unmounts the page, so this place cannot live in component state
const places = new Map<string | null, ReviewCommentsPlace>();

function writePlace(
  reviewId: string,
  patch: Partial<ReviewCommentsPlace>,
): void {
  const current = places.get(reviewId);
  places.set(reviewId, {
    scrollTop: patch.scrollTop ?? current?.scrollTop ?? 0,
    commentId:
      patch.commentId !== undefined
        ? patch.commentId
        : (current?.commentId ?? null),
  });
}

export function rememberedReviewCommentId(
  reviewId: string | null,
): string | null {
  return places.get(reviewId)?.commentId ?? null;
}

export function clearReviewCommentsPlaces(): void {
  places.clear();
}

export function useRememberReviewCommentSelection(
  reviewId: string | null,
  reviewLoaded: boolean,
  commentId: string | null,
): void {
  useEffect(() => {
    if (!reviewLoaded || reviewId == null || reviewId.length === 0) return;
    writePlace(reviewId, { commentId });
  }, [commentId, reviewId, reviewLoaded]);
}

export function usePersistedReviewCommentsScroll(reviewId: string) {
  return useMemo(() => {
    let node: HTMLElement | null = null;
    let onScroll = () => {};
    return (next: HTMLElement | null) => {
      if (node != null) {
        node.removeEventListener("scroll", onScroll);
        writePlace(reviewId, { scrollTop: node.scrollTop });
      }
      node = next;
      if (next == null) return;
      const saved = places.get(reviewId);
      if (saved != null) next.scrollTop = saved.scrollTop;
      onScroll = () => {
        writePlace(reviewId, { scrollTop: next.scrollTop });
      };
      next.addEventListener("scroll", onScroll);
    };
  }, [reviewId]);
}
